import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Package, Image, Mail, Users, FileText, Lightbulb,
  RefreshCw, TrendingUp, ShoppingBag, Eye, Clock,
  CheckCircle, XCircle, AlertCircle, ArrowUpRight, Activity, Lock
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const PALETTE = {
  blue:   '#4F8EF7',
  green:  '#10B981',
  amber:  '#F59E0B',
  rose:   '#F43F5E',
  purple: '#8B5CF6',
  orange: '#F97316',
};

const fmt = n => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n);

function StatCard({ label, value, icon: Icon, color, bg, to, trend }) {
  return (
    <Link to={to}
      className="group relative overflow-hidden bg-white rounded-2xl p-5 shadow-sm border border-gray-100
                 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
      <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full opacity-10 group-hover:opacity-20 transition-opacity"
        style={{ background: color }} />
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm" style={{ background: bg }}>
          <Icon size={20} style={{ color }} />
        </div>
        <ArrowUpRight size={14} className="text-gray-300 group-hover:text-gray-500 transition-colors mt-1" />
      </div>
      <div className="mt-4">
        <p className="text-3xl font-bold text-gray-800 tracking-tight">{fmt(value)}</p>
        <p className="text-xs text-gray-400 font-medium tracking-widest uppercase mt-1">{label}</p>
      </div>
      {trend && (
        <div className="mt-3 flex items-center gap-1">
          <TrendingUp size={11} className="text-emerald-500" />
          <span className="text-xs text-emerald-500 font-medium">{trend}</span>
        </div>
      )}
    </Link>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-xl px-3 py-2 shadow-xl">
      {label && <p className="text-gray-400 mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  );
};

function StatusBadge({ status }) {
  const map = {
    pending:  { color: 'text-amber-600 bg-amber-50',    icon: Clock,        label: 'Pending' },
    approved: { color: 'text-emerald-600 bg-emerald-50', icon: CheckCircle, label: 'Approved' },
    rejected: { color: 'text-rose-600 bg-rose-50',      icon: XCircle,     label: 'Rejected' },
  };
  const { color, icon: Icon, label } = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${color}`}>
      <Icon size={10} /> {label}
    </span>
  );
}

/* Permission-locked placeholder card */
function LockedCard({ label }) {
  return (
    <div className="relative overflow-hidden bg-gray-50 rounded-2xl p-5 border border-dashed border-gray-200">
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gray-100">
          <Lock size={16} className="text-gray-300" />
        </div>
      </div>
      <div className="mt-4">
        <p className="text-2xl font-bold text-gray-200">—</p>
        <p className="text-xs text-gray-300 font-medium tracking-widest uppercase mt-1">{label}</p>
      </div>
      <p className="text-xs text-gray-300 mt-2">No access</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { can, isMasterAdmin, user } = useAuth();
  const [stats, setStats] = useState({ products: 0, contacts: 0, users: 0, submissions: [], solutions: 0 });
  const [recentSubs, setRecentSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(new Date());

  const load = useCallback(() => {
    setLoading(true);
    const calls = [
      can('products', 'view')    ? api.get('/products?limit=1')  : Promise.resolve(null),
      can('contacts', 'view')    ? api.get('/contact')            : Promise.resolve(null),
      isMasterAdmin              ? api.get('/users')              : Promise.resolve(null),
      can('submissions', 'view') || !isMasterAdmin ? api.get('/submissions') : Promise.resolve(null),
      can('solutions', 'view')   ? api.get('/solutions')          : Promise.resolve(null),
    ];

    Promise.allSettled(calls).then(([p, co, u, s, sol]) => {
      const allSubs = s.value?.data?.submissions || [];
      setStats({
        products:    p.value?.data?.total || 0,
        contacts:    co.value?.data?.contacts?.length || 0,
        users:       u.value?.data?.users?.length || 0,
        submissions: allSubs,
        solutions:   sol.value?.data?.solutions?.length || 0,
      });
      setRecentSubs(allSubs.slice(0, 5));
      setLastSync(new Date());
      setLoading(false);
    });
  }, [can, isMasterAdmin]);

  useEffect(() => { load(); }, [load]);

  const pending  = stats.submissions.filter(s => s.status === 'pending').length;
  const approved = stats.submissions.filter(s => s.status === 'approved').length;
  const rejected = stats.submissions.filter(s => s.status === 'rejected').length;
  const totalSubs = stats.submissions.length;
  const syncAgo = Math.round((new Date() - lastSync) / 60000);

  const ALL_CARDS = [
    { label: 'Products',  value: stats.products,  icon: Package,   color: PALETTE.blue,   bg: '#EFF6FF', to: '/admin/products',    trend: 'Active catalogue', perm: ['products','view'] },
    { label: 'Solutions', value: stats.solutions, icon: Lightbulb, color: PALETTE.amber,  bg: '#FFFBEB', to: '/admin/solutions',   trend: 'Live solutions',   perm: ['solutions','view'] },
    { label: 'Contacts',  value: stats.contacts,  icon: Mail,      color: PALETTE.green,  bg: '#F0FDF4', to: '/admin/contacts',    trend: 'Inquiries',        perm: ['contacts','view'] },
    { label: 'Users',     value: stats.users,     icon: Users,     color: PALETTE.rose,   bg: '#FFF1F2', to: '/admin/users',       trend: 'Registered',       masterOnly: true },
    { label: 'Pending',   value: pending,         icon: FileText,  color: PALETTE.orange, bg: '#FFF7ED', to: '/admin/submissions', trend: pending > 0 ? `${pending} need review` : 'All clear', perm: ['submissions','view'] },
  ];

  const visibleCards = ALL_CARDS.filter(c =>
    c.masterOnly ? isMasterAdmin : can(c.perm[0], c.perm[1])
  );

  /* Bar chart — only permitted modules */
  const barData = [
    can('products','view')  && { name: 'Products',  value: stats.products,  fill: PALETTE.blue },
    can('solutions','view') && { name: 'Solutions', value: stats.solutions, fill: PALETTE.amber },
    can('contacts','view')  && { name: 'Contacts',  value: stats.contacts,  fill: PALETTE.green },
    isMasterAdmin           && { name: 'Users',     value: stats.users,     fill: PALETTE.rose },
  ].filter(Boolean);

  const totalItems = barData.reduce((a, b) => a + b.value, 0);

  /* Area chart — only show lines for permitted data */
  const areaData = [
    { day: 'Mon', products: 2 },
    { day: 'Tue', products: 1 },
    { day: 'Wed', products: 3 },
    { day: 'Thu', products: 1 },
    { day: 'Fri', products: 2 },
    { day: 'Sat', products: 0 },
    { day: 'Sun', products: 1 },
  ];

  const pieData = [
    { name: 'Pending',  value: pending,  color: PALETTE.amber },
    { name: 'Approved', value: approved, color: PALETTE.green },
    { name: 'Rejected', value: rejected, color: PALETTE.rose },
  ].filter(d => d.value > 0);

  const roleLabel = isMasterAdmin ? 'Master Admin' : 'Web Manager';
  const roleColor = isMasterAdmin ? '#B8A830' : '#4F8EF7';

  return (
    <div className="space-y-6 pb-8">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Welcome back, <span className="font-medium" style={{ color: roleColor }}>{user?.name}</span>
            {' '}— {roleLabel} view
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 flex items-center gap-1.5">
            <Activity size={12} className={loading ? 'animate-pulse text-emerald-500' : 'text-gray-300'} />
            {loading ? 'Syncing…' : `Updated ${syncAgo === 0 ? 'just now' : `${syncAgo}m ago`}`}
          </span>
          <button onClick={load}
            className="flex items-center gap-1.5 text-xs font-medium px-4 py-2 rounded-xl border border-gray-200
                       hover:border-yellow-400 hover:text-yellow-600 transition-all shadow-sm bg-white">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Permission notice for managers */}
      {!isMasterAdmin && (
        <div className="bg-blue-50 border border-blue-100 rounded-2xl px-5 py-3 flex items-center gap-3">
          <Lock size={14} className="text-blue-400 shrink-0" />
          <p className="text-xs text-blue-600">
            You have access to <strong>{visibleCards.length}</strong> module{visibleCards.length !== 1 ? 's' : ''} as approved by Master Admin.
            Sections not shown require additional permissions.
          </p>
        </div>
      )}

      {/* Stat cards — only permitted */}
      {visibleCards.length > 0 ? (
        <div className={`grid grid-cols-2 gap-4 ${visibleCards.length <= 2 ? 'md:grid-cols-2' : visibleCards.length <= 4 ? 'md:grid-cols-4' : 'md:grid-cols-3 xl:grid-cols-6'}`}>
          {visibleCards.map(card => <StatCard key={card.label} {...card} />)}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
          <Lock size={32} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">No modules assigned yet</p>
          <p className="text-xs text-gray-300 mt-1">Contact your Master Admin to grant access</p>
        </div>
      )}

      {/* Charts row — only if user has at least one data permission */}
      {visibleCards.length > 0 && (
        <div className="grid lg:grid-cols-3 gap-4">

          {/* Area chart */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-semibold text-gray-800">Weekly Activity</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {[can('products','view') && 'Products', can('collections','view') && 'Collections'].filter(Boolean).join(' · ')} this week
                </p>
              </div>
              <span className="text-xs bg-emerald-50 text-emerald-600 font-medium px-2.5 py-1 rounded-full">Live</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={areaData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gBlue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={PALETTE.blue}   stopOpacity={0.25} />
                    <stop offset="95%" stopColor={PALETTE.blue}   stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gPurple" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={PALETTE.purple} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={PALETTE.purple} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                {can('products','view') && (
                  <Area type="monotone" dataKey="products" name="Products" stroke={PALETTE.blue} fill="url(#gBlue)" strokeWidth={2.5} dot={false} />
                )}
                {can('collections','view') && (
                  <Area type="monotone" dataKey="collections" name="Collections" stroke={PALETTE.purple} fill="url(#gPurple)" strokeWidth={2.5} dot={false} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Submissions pie OR locked */}
          {can('submissions','view') || !isMasterAdmin ? (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col">
              <div className="mb-4">
                <h2 className="font-semibold text-gray-800">Submissions</h2>
                <p className="text-xs text-gray-400 mt-0.5">Status breakdown</p>
              </div>
              {totalSubs === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-300 gap-2">
                  <AlertCircle size={32} />
                  <p className="text-xs">No submissions yet</p>
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70}
                        paddingAngle={3} dataKey="value" stroke="none">
                        {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-auto space-y-2">
                    {[
                      { label: 'Pending',  value: pending,  color: PALETTE.amber },
                      { label: 'Approved', value: approved, color: PALETTE.green },
                      { label: 'Rejected', value: rejected, color: PALETTE.rose },
                    ].map(({ label, value, color }) => (
                      <div key={label} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                          <span className="text-gray-500 text-xs">{label}</span>
                        </div>
                        <span className="font-semibold text-gray-700 text-xs">{value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-2xl p-6 border border-dashed border-gray-200 flex flex-col items-center justify-center gap-3">
              <Lock size={24} className="text-gray-200" />
              <p className="text-xs text-gray-300 text-center">Submissions not accessible<br />with current permissions</p>
            </div>
          )}
        </div>
      )}

      {/* Bar chart — only if has data */}
      {barData.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-semibold text-gray-800">Content Overview</h2>
              <p className="text-xs text-gray-400 mt-0.5">Total items in your accessible modules</p>
            </div>
            <span className="text-xs text-gray-400 bg-gray-50 px-2.5 py-1 rounded-full font-medium">{totalItems} total</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={barData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }} barSize={40}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F9FAFB' }} />
              <Bar dataKey="value" name="Count" radius={[6, 6, 0, 0]}>
                {barData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Recent submissions — only if permitted OR manager (always show their own) */}
      {(can('submissions','view') || !isMasterAdmin) && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-800">Recent Submissions</h2>
              <p className="text-xs text-gray-400 mt-0.5">Latest user-submitted content</p>
            </div>
            <Link to="/admin/submissions" className="text-xs font-medium text-yellow-600 hover:text-yellow-700 flex items-center gap-1">
              View all <ArrowUpRight size={12} />
            </Link>
          </div>
          {recentSubs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-300 gap-2">
              <ShoppingBag size={32} />
              <p className="text-xs">No submissions yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recentSubs.map(sub => (
                <div key={sub._id} className="px-6 py-3.5 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-xs font-bold shrink-0">
                    {sub.user?.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{sub.user?.name || 'Unknown'}</p>
                    <p className="text-xs text-gray-400 capitalize">{sub.type} submission</p>
                  </div>
                  <StatusBadge status={sub.status} />
                  <span className="text-xs text-gray-300 shrink-0">
                    {new Date(sub.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Master admin system summary */}
      {isMasterAdmin && (
        <>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-lg font-bold shadow-md"
              style={{ background: 'linear-gradient(135deg, #B8A830, #d4af37)' }}>
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-gray-800">{user?.name}</p>
              <p className="text-xs text-gray-400">{user?.email}</p>
            </div>
          </div>
          <Link to="/admin/profile"
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl border border-yellow-300 text-yellow-700 hover:bg-yellow-50 transition-colors">
            <Users size={14} /> Personal Details
          </Link>
        </div>
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">System Summary</h2>
            <Eye size={16} className="text-gray-400" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Content',    value: totalItems,       icon: Package,  color: PALETTE.blue },
              { label: 'Registered Users', value: stats.users,      icon: Users,    color: PALETTE.rose },
              { label: 'Pending Review',   value: pending,          icon: Clock,    color: PALETTE.amber },
              { label: 'Total Submissions',value: totalSubs,        icon: FileText, color: PALETTE.purple },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="bg-white/5 rounded-xl p-4 hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={14} style={{ color }} />
                  <span className="text-xs text-gray-400">{label}</span>
                </div>
                <p className="text-2xl font-bold">{fmt(value)}</p>
              </div>
            ))}
          </div>
        </div>
        </>
      )}
    </div>
  );
}
